import React from 'react';
import { useLatest, LatestList } from '../components/Latest';
import BookPanel from '../components/BookPanel';
import {
  PageHead, Section, Eyebrow, PageTitle, Lede, SectionHead, Button, Card, CardTitle, CardBody, Grid, Saying, SUBSTACK_URL, CONTACT_MAILTO,
} from '../components/ui';

/** Writing and speaking. The Substack feed, the keynote, the book. */

const TALKS = [
  {
    date: 'April 2026',
    kind: 'Executive keynote',
    title: 'The Urgent Need for AI Fluency',
    body: 'The case for AI Fluency as a human capability, not a technology rollout. Delivered to executive teams and leadership audiences.',
  },
];

const FORMATS = [
  ['Keynote', '60 minutes', 'Conference and event audiences, tuned to the room.'],
  ['Board and leadership session', '60 to 90 minutes', 'Boards and leadership teams, closed room.'],
  ['Working session', '4 hours', 'Intact teams, hands on the work.'],
  ['Executive day', 'One day', 'Executive cohorts, a full day at speed.'],
];

const ThoughtLeadershipPage = () => {
  const items = useLatest(12);
  return (
    <>
      <PageHead
        title="What we think"
        description="The book Thinking with AI, the Substack, and the talks and articles. Judgment, capability, and what leaders give up when they let the machine do the thinking."
        path="/what-we-think"
      />
      <Section tone="page" reveal={false}>
        <Eyebrow>What we think</Eyebrow>
        <PageTitle>The argument, in the open.</PageTitle>
        <Lede>We put the case for cognitive infrastructure between covers, publish it on Substack, and take it to stages. Read it, or bring it to your room.</Lede>
        <Saying className="mt-10">AI is most dangerous not when it fails, but when it works so well that people quietly stop thinking for themselves.</Saying>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button to="/book">The book</Button>
          <Button href={SUBSTACK_URL} variant="secondary">Read the Substack</Button>
          <Button href={CONTACT_MAILTO} variant="secondary">Book a talk</Button>
        </div>
      </Section>

      <div id="book" className="scroll-mt-20"><BookPanel /></div>

      <Section tone="page" id="substack" className="scroll-mt-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Eyebrow>Substack</Eyebrow>
            <SectionHead>Recent essays.</SectionHead>
          </div>
          <Button href={SUBSTACK_URL} variant="ghost">All essays on Substack</Button>
        </div>
        {items.length ? <LatestList items={items} /> : <p className="font-sans text-interface">Essays publish on Substack at rightbrainedai.substack.com.</p>}
      </Section>

      <Section tone="card" id="articles" className="scroll-mt-20">
        <Eyebrow>Talks and articles</Eyebrow>
        <SectionHead>One argument, many rooms.</SectionHead>
        <p className="prose-rbl mt-5">Srini gives one keynote and adapts it to the room. Most AI talks tell an audience the tools are coming and they had better keep up. This one starts with what happens to a person, a team, or a region when the thing that built their capability quietly moves somewhere else. Ohio has lived that story once already.</p>
        <Grid cols={4} className="mt-8">
          {FORMATS.map(([name, length, who]) => (
            <Card key={name}>
              <CardTitle>{name}</CardTitle>
              <div className="font-mono text-meta mt-1 text-navy-500 dark:text-navy-400">{length}</div>
              <CardBody>{who}</CardBody>
            </Card>
          ))}
        </Grid>
        <div className="mt-8 flex flex-col gap-[14px]">
          {TALKS.map((t) => (
            <Card key={t.title} accent as="article">
              <div className="font-mono text-meta text-navy-500 dark:text-navy-400">{t.date}. {t.kind}</div>
              <CardTitle className="mt-1">{t.title}</CardTitle>
              <CardBody>{t.body}</CardBody>
            </Card>
          ))}
        </div>
        <div className="mt-8"><Button href={CONTACT_MAILTO}>Book Srini to speak</Button></div>
      </Section>
    </>
  );
};

export default ThoughtLeadershipPage;
